import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import React, { useEffect, useRef, useState } from 'react';
import {
    IconChevronSprite,
    Polyline1,
    Polyline2,
    Polyline3,
    Polyline4,
    Polyline5,
} from '$partials/icons/index.js';
import { Button } from '$partials/ui';
import Image from '$partials/media/image';
import { useLocale } from '$contexts/locale.jsx';
gsap.registerPlugin(ScrollTrigger);

/**
 * @param {any} richText
 * @returns {React.ReactNode}
 */
const renderHeroText = (richText) => {
    if (!richText?.json?.content) {
        return null;
    }

    return richText.json.content
        .filter((/** @type {any} */ block) => block.content?.length)
        .map((/** @type {any} */ block, /** @type {number} */ i) => (
            <div
                className='animated-statement__lines'
                key={i}>
                {block.content.map((/** @type {any} */ node, /** @type {number} */ j) => {
                    if (node.nodeType !== 'text') {
                        return null;
                    }
                    const isBold = node.marks?.some((/** @type {any} */ mark) => mark.type === 'bold');
                    return isBold ? (
                        <span
                            className='animated-statement__highlight animated-statement__highlight--orange'
                            key={j}>
                            {node.value}
                        </span>
                    ) : (
                        <React.Fragment key={j}>{node.value}</React.Fragment>
                    );
                })}
            </div>
        ));
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleStatement}
 */

const ModuleStatement = ({
    alignment = 'Left',
    bodyCopy = '',
    cta1Link,
    cta1Label,
    cta2Link,
    cta2Label,
    eyebrow = '',
    eyebrowIcon,
    heroText,
    headingTextSize,
    polyline,
    theme = '',
    variant,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    footer,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    previousTypename,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    isArticlePage,
}) => {

    const polyLineIcons = [Polyline1, Polyline2, Polyline3, Polyline4, Polyline5];
    const selectedPolyline = polyline === 'None' ? null : polyLineIcons[Number(polyline) - 1] || null;

    const {
        formatURI,
        translate: t,
    } = useLocale();

    const resolvedVariant = variant || 'A (animated text)';

    const collapseTopPadding = previousTypename === 'BvaModuleSingleImage';
    const topPaddingClass = collapseTopPadding ? ' pt-16' : ` pt-40${isArticlePage ? '' : ' md:pt-80'}`;

    const isCenter = alignment === 'Center';
    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();

    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            gsap.set(containerRef.current.querySelectorAll('.animation__mask-slide-in'), { opacity: 0 });
            gsap.set(containerRef.current.querySelectorAll('.animated-statement__text'), { opacity: 1 });

            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create reveal hide of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 2.8,
                    ease: 'power3',
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animated-statement__lines'),
                {
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );
        }
    };

    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    // render
    return (
        resolvedVariant === 'A (animated text)' ? (
            <section
                className={`overflow-hidden animated-statement relative${footer ? '' : ` pb-40${isArticlePage ? '' : ' md:pb-80'}`}${topPaddingClass}`}
                ref={containerRef}>
                <div className='relative z-10 layout-container layout-site-outer-padding w-full overflow-hidden'>
                    <div className={`relative theme-${theme} ${theme !== 'transparent' ? 'bg-theme-themed-surface-bg py-40 md:py-80' : ''} ${footer ? 'theme-offset' : 'px-32 md:px-40'} rounded`}>

                        {/* polyline */}
                        {selectedPolyline && theme !== 'transparent' && (
                            <div
                                aria-hidden='true'
                                className='absolute top-0 right-0 w-[50dvw] z-0'>
                                {React.createElement(selectedPolyline, {
                                    className: 'w-full h-auto text-theme-themed-polyline'
                                })}
                            </div>
                        )}

                        <div className={`relative z-[1] flex flex-col${isCenter ? ' items-center text-center' : ''}`}>

                            {/* eyebrow */}
                            {eyebrow && (
                                <p className='col-padding animation__mask-slide-in type-style-eyebrow text-theme-themed-text-accent mb-24'>
                                    {eyebrow}
                                </p>
                            )}

                            {/* hero text */}
                            {heroText && (
                                <div className={`
                                    ${headingTextSize === 'Hero' ? 'type-style-display-animated md:w-8/12' : 'type-style-1-extended md:w-11/12'}
                                    col-padding w-full sm:w-11/12 break-words text-theme-themed-text-accent animated-statement__text opacity-0`}>
                                    {renderHeroText(heroText)}
                                </div>
                            )}

                            {/* body copy */}
                            {bodyCopy && (
                                <p className='col-padding animation__mask-slide-in text-text-secondary type-style-body mt-24 sm:w-10/12 md:w-8/12'>
                                    {bodyCopy}
                                </p>
                            )}

                            {/* cta buttons */}
                            {(cta1Link || cta2Link) && (
                                <div className={`col-padding animation__mask-slide-in flex flex-wrap gap-8 mt-24${isCenter ? ' justify-center' : ''}`}>
                                    {cta1Link && (
                                        <Button
                                            as='a'
                                            className='button button--primary'
                                            href={formatURI(cta1Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta1Label}</span>
                                            </span>
                                            <span className='button__icon button__icon--animate'>
                                                <IconChevronSprite />
                                            </span>
                                        </Button>
                                    )}
                                    {cta2Link && (
                                        <Button
                                            as='a'
                                            className='button button--secondary'
                                            href={formatURI(cta2Link)}>
                                            <span className='button__label--animate'>
                                                <span>{cta2Label}</span>
                                            </span>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

        ) : (

            // variant B (static text)
            <section
                className={`relative pb-40${isArticlePage ? '' : ' md:pb-80'} bg-white rounded${topPaddingClass}`}>
                <div className='relative z-10 layout-container layout-site-outer-padding w-full overflow-hidden'>
                    <div className={`flex flex-col ${isCenter ? ' items-center text-center' : ''}`}>

                        {/* eyebrow */}
                        {eyebrowIcon && (
                            <Image
                                containerClassName='mb-8'
                                src={eyebrowIcon?.src}
                                style={{ height: '48px', width: 'auto' }}/>
                        )}
                        {eyebrow && (
                            <p className='type-style-eyebrow text-text-accent mb-16'>
                                {eyebrow}
                            </p>
                        )}

                        {/* hero text */}
                        {heroText && (
                            <div className='w-full sm:w-11/12 md:w-7/12 type-style-2-extended break-words text-secondary'>
                                {renderHeroText(heroText)}
                            </div>
                        )}

                        {/* body copy */}
                        {bodyCopy && (
                            <p className='text-text-secondary type-style-body mt-16 sm:w-10/12 md:w-8/12'>
                                {bodyCopy}
                            </p>
                        )}

                        {/* cta buttons */}
                        {(cta1Link || cta2Link) && (
                            <div className={`flex flex-wrap gap-8 mt-32${isCenter ? ' justify-center' : ''}`}>
                                {cta1Link && (
                                    <Button
                                        as='a'
                                        className='button button--primary'
                                        href={formatURI(cta1Link)}>
                                        <span className='button__label--animate'>
                                            <span>{cta1Label}</span>
                                        </span>
                                        <span className='button__icon button__icon--animate'>
                                            <IconChevronSprite />
                                        </span>
                                    </Button>
                                )}
                                {cta2Link && (
                                    <Button
                                        as='a'
                                        className='button button--secondary'
                                        href={formatURI(cta2Link)}>
                                        <span className='button__label--animate'>
                                            <span>{cta2Label}</span>
                                        </span>
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            </section>
        )
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ModuleStatement;
